import { useRef, useEffect, useState, ReactNode } from 'react';
import { ProductShareCard } from './cards/ProductShareCard';
import { PollShareCard } from './cards/PollShareCard';
import { LookShareCard } from './cards/LookShareCard';
import { CollectionShareCard } from './cards/CollectionShareCard';
import { CelebrityShareCard } from './cards/CelebrityShareCard';
import { StyleProfileShareCard } from './cards/StyleProfileShareCard';
import { PriceGuessShareCard } from './cards/PriceGuessShareCard';
import { OutfitChallengeShareCard } from './cards/OutfitChallengeShareCard';

export type ShareCardType = 'product' | 'poll' | 'look' | 'collection' | 'celebrity' | 'style-profile' | 'price-guess' | 'outfit-challenge';

interface CelebrityMatchData {
  name: string;
  matchPercentage: number;
  knownFor: string;
  imageUrl?: string;
}

interface PriceGuessRoundData {
  itemName: string;
  itemImage?: string;
  guessedPrice: number;
  actualPrice: number;
  currency: string;
  scoreTier: string;
}

interface OutfitItemData {
  name: string;
  imageUrl?: string;
  category: string;
}

export interface ShareCardData {
  type: ShareCardType;
  title: string;
  imageUrl?: string;
  images?: string[]; // For polls/collections with multiple images
  subtitle?: string; // Price, vote count, item count
  userName?: string;
  storeName?: string;
  // Celebrity match data
  celebrityMatches?: CelebrityMatchData[];
  vibe?: string;
  // Style profile data
  bodyShape?: string;
  bodyShapeConfidence?: number;
  colorPalette?: string[];
  skinTone?: string;
  undertone?: string;
  estimatedSize?: string;
  styleDos?: string[];
  styleDonts?: string[];
  // Price guess data
  totalScore?: number;
  avgAccuracy?: number;
  perfectCount?: number;
  streak?: number;
  level?: number;
  levelTitle?: string;
  priceGuessRounds?: PriceGuessRoundData[];
  // Outfit challenge data
  score?: number;
  starRating?: number;
  difficulty?: string;
  stylistSummary?: string;
  outfitItems?: OutfitItemData[];
}

interface ShareCardCanvasProps {
  data: ShareCardData;
  onCapture: (blob: Blob) => void;
  onError: (error: Error) => void;
}

// Convert external image URL to base64 data URL to avoid CORS issues
async function imageToDataUrl(url: string): Promise<string> {
  try {
    const response = await fetch(url, { mode: 'cors' });
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.warn('Failed to convert image to data URL:', url, error);
    return url; // Fallback to original URL
  }
}

export function ShareCardCanvas({ data, onCapture, onError }: ShareCardCanvasProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const [processedData, setProcessedData] = useState<ShareCardData | null>(null);

  // Pre-process images to convert to data URLs
  useEffect(() => {
    const processImages = async () => {
      try {
        const newData = { ...data };
        
        // Convert main image
        if (data.imageUrl) {
          newData.imageUrl = await imageToDataUrl(data.imageUrl);
        }
        
        // Convert multiple images
        if (data.images && data.images.length > 0) {
          newData.images = await Promise.all(
            data.images.map(url => imageToDataUrl(url))
          );
        }
        
        setProcessedData(newData);
      } catch (error) {
        console.error('Failed to process images:', error);
        // Use original data as fallback
        setProcessedData(data);
      }
    };

    processImages();
  }, [data]);

  // Capture the card once images are processed
  useEffect(() => {
    if (!processedData || !cardRef.current) return;

    const captureCard = async () => {
      try {
        // Delay to ensure images render
        await new Promise(resolve => setTimeout(resolve, 300));

        // Dynamically import html-to-image to keep it out of main bundle (saves 200KB)
        const { toPng } = await import('html-to-image');

        const dataUrl = await toPng(cardRef.current!, {
          quality: 1,
          pixelRatio: 2,
          cacheBust: false,
          skipAutoScale: true,
        });
        
        // Convert data URL to Blob
        const response = await fetch(dataUrl);
        const blob = await response.blob();
        onCapture(blob);
      } catch (error) {
        console.error('Failed to capture share card:', error);
        onError(error as Error);
      }
    };

    captureCard();
  }, [processedData, onCapture, onError]);

  const renderCard = (): ReactNode => {
    if (!processedData) return null;
    
    switch (processedData.type) {
      case 'product':
        return (
          <ProductShareCard
            title={processedData.title}
            imageUrl={processedData.imageUrl}
            subtitle={processedData.subtitle}
            storeName={processedData.storeName}
          />
        );
      case 'poll':
        return (
          <PollShareCard
            title={processedData.title}
            images={processedData.images}
            userName={processedData.userName}
          />
        );
      case 'look':
        return (
          <LookShareCard
            imageUrl={processedData.imageUrl}
            userName={processedData.userName}
          />
        );
      case 'collection':
        return (
          <CollectionShareCard
            title={processedData.title}
            images={processedData.images}
            subtitle={processedData.subtitle}
          />
        );
      case 'celebrity':
        return (
          <CelebrityShareCard
            imageUrl={processedData.imageUrl}
            matches={processedData.celebrityMatches}
            vibe={processedData.vibe}
            userName={processedData.userName}
          />
        );
      case 'style-profile':
        return (
          <StyleProfileShareCard
            imageUrl={processedData.imageUrl}
            vibe={processedData.vibe}
            bodyShape={processedData.bodyShape}
            bodyShapeConfidence={processedData.bodyShapeConfidence}
            colorPalette={processedData.colorPalette}
            skinTone={processedData.skinTone}
            undertone={processedData.undertone}
            estimatedSize={processedData.estimatedSize}
            styleDos={processedData.styleDos}
            styleDonts={processedData.styleDonts}
            userName={processedData.userName}
          />
        );
      case 'price-guess':
        return (
          <PriceGuessShareCard
            totalScore={processedData.totalScore || 0}
            avgAccuracy={processedData.avgAccuracy || 0}
            perfectCount={processedData.perfectCount || 0}
            streak={processedData.streak || 0}
            level={processedData.level || 1}
            levelTitle={processedData.levelTitle}
            rounds={processedData.priceGuessRounds}
          />
        );
      case 'outfit-challenge':
        return (
          <OutfitChallengeShareCard
            theme={processedData.title}
            score={processedData.score || 0}
            starRating={processedData.starRating || 0}
            difficulty={processedData.difficulty}
            stylistSummary={processedData.stylistSummary}
            items={processedData.outfitItems}
            streak={processedData.streak}
            level={processedData.level}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        left: '-9999px',
        top: 0,
        pointerEvents: 'none',
      }}
    >
      <div
        ref={cardRef}
        style={{
          width: 1080,
          height: 1920,
          backgroundColor: '#0a0a0a',
          fontFamily: 'Sora, sans-serif',
        }}
      >
        {renderCard()}
      </div>
    </div>
  );
}
